/**
 * Geosoft QC runs (phase 3, docs/specs/geosoft-qc.md): the C208, C210 and helicopter QC script families of
 * `GEOSOFT_QC_SCRIPTS_ANALYSIS.md` turned into data. Teslyn's Geosoft driver (`crates/teslyn-geosoft`, `qc.rs`)
 * runs Geosoft's contract QC GXs on a copy of the flight database and evaluates the tests built here, line by line.
 *
 * Where the values come from:
 * - **Contract tests** (a failed one means the line needs a decision): the project's QC limits ("Reflight
 *   specifications", decision 13; tenant defaults when the project has none, decision 123): flight path, terrain
 *   clearance against the block's planned clearance, 4th-difference mag noise on the tail sensor, sample interval;
 *   each counts only over its "for more than … m" distance, as Tylor's parser summary did.
 * - **Checks** (a warning the operator looks at): the fixed bands the operators read off the five review views, per
 *   aircraft system family, counted at every fiducial; fiducials with fewer satellites than the project's minimum
 *   (6 by default, decision 180).
 * - **Daily mag noise checks** (decision 179): the field staff's per-line check of the 4th difference of each
 *   magnetometer (`MagTF1U_ED_D4` tail, `MagTF2U_ED_D4` and `MagTF3U_ED_D4` wingtips on the C208) against the
 *   contract's noise limit, counted only when it lasts more than 2 s. The 2-second rule applies to these checks only,
 *   not to the Geosoft QC utility menu checks or the other review-view checks.
 * - **Heights from the crew**: hard deck and lower working height, entered after the safety recce flight. Flying
 *   below the hard deck is a fail (decision 181): Line review accepts such a line only with a typed reason.
 *
 * No constant here is a survey value: survey height, limits and heights come from the project.
 */
import type { Block, Common, IsoDateTime, Project, Uuid } from './model.js';
import { expectedEpsg } from './geosoft-projection.js';
import { formatZfid, zfidRangeText } from './zfid.js';
import { qcFlightPathChecks, type BlockLinesGdb } from './qc-daily.js';
import {
  BENZ_F26048_QC_RULES,
  isCessna208,
  qcDefaultsForAircraftType,
  qcRule,
  type QcRule,
} from './qc-spec.js';

export type QcFamily = 'c208' | 'c210' | 'heli';

/** One aircraft system family's channel names and review-view bands (GEOSOFT_QC_SCRIPTS_ANALYSIS.md §2, §3). */
export interface QcFamilyProfile {
  readonly family: QcFamily;
  readonly label: string;
  /** Script family folder the profile is ported from. */
  readonly scripts: string;
  readonly x: string;
  readonly y: string;
  /** The edited radar altimeter (house rule: always QC the `_ED` channel). */
  readonly radalt: string;
  /** The tail magnetometer, edited (lag only, no filtering: the contract's "raw" channel). */
  readonly mag: string;
  readonly magD4: string;
  /**
   * The 4th-difference channels of the daily mag noise checks (decision 179), tail first. MB named them
   * `MagTF1_ED_D4`, `MagTF2_ED_D4`, `MagTF3_ED_D4`; every Thomson database on file (199 under C:\TA_Survey) calls
   * them `MagTF1U_ED_D4` and so on, so those are the defaults. A project can name its own (`qc_noise_channels`).
   */
  readonly noiseD4: readonly string[];
  /**
   * The edited magnetometers, tail first (decision 504's comparisons: the tail against the fluxgate's `VecTF`, and
   * on the C208 the three against each other).
   */
  readonly mags: readonly string[];
  /** GPS satellites used (view 2 `Sats`). */
  readonly sats: string;
  readonly eastD1: string;
  readonly northD1: string;
  /** GPS position jump band, ± m per fid (view 1: ±5 C208, ±3 C210 and helicopter). */
  readonly gpsJumpM: number;
  readonly gpsHeightD1: string;
  /** GPS height jump band, ± m per fid (view 2). */
  readonly gpsHeightJumpM: number;
  /** Expected down-crystal counts (view 3). */
  readonly downCrystals: readonly number[];
  /** Spectrometer sample time band, ms (view 3). */
  readonly sampMs: readonly [number, number];
  /**
   * Speed band, m/s (`Flag_Speed` of script 1), when the family computes `Speed`; `min: null` checks the maximum
   * only. Helicopters: maximum only, 66.9 m/s (130 knots; decision 983, Q872 B, replacing decision 200's 77 m/s), the
   * top of the final `Heli_Script_1_V1.00`'s 20.6 to 66.9 m/s flag (decision 969); no minimum, so slow turns never warn.
   */
  readonly speed: { readonly min: number | null; readonly max: number } | null;
}

/**
 * Decision 983 (Q872 B, 3 Nov 2026): Flight data QC warns when a helicopter's speed is above 66.9 m/s, 130 knots
 * (130 x 0.514444 = 66.878, to one decimal as the final `Heli_Script_1_V1.00` flags it), with no minimum. It replaced
 * decision 200's 77 m/s.
 */
export const HELI_SPEED_MAX_MS = 66.9;

export const QC_FAMILIES: readonly QcFamilyProfile[] = [
  {
    family: 'c208',
    label: 'Cessna 208 (three magnetometers)',
    scripts: 'QC_Mag_Database_Gradient',
    x: 'Easting_ED',
    y: 'Northing_ED',
    radalt: 'RadAlt_mED',
    mag: 'MagTF1U_ED',
    magD4: 'MagTF1U_ED_D4',
    noiseD4: ['MagTF1U_ED_D4', 'MagTF2U_ED_D4', 'MagTF3U_ED_D4'],
    mags: ['MagTF1U_ED', 'MagTF2U_ED', 'MagTF3U_ED'],
    sats: 'Sats',
    eastD1: 'Easting_ED_D1',
    northD1: 'Northing_ED_D1',
    gpsJumpM: 5,
    gpsHeightD1: 'GpsHt_ED_D1',
    gpsHeightJumpM: 2,
    downCrystals: [12],
    sampMs: [490, 510],
    speed: null,
  },
  {
    family: 'c210',
    label: 'Cessna 210 (one magnetometer)',
    scripts: 'C210_Scripts_V2.10',
    x: 'East_ED',
    y: 'North_ED',
    radalt: 'Radalt_ED',
    mag: 'MagTF1U_ED',
    magD4: 'MagTF1U_ED_D4',
    noiseD4: ['MagTF1U_ED_D4'],
    mags: ['MagTF1U_ED'],
    sats: 'Sats',
    eastD1: 'East_ED_D1',
    northD1: 'North_ED_D1',
    gpsJumpM: 3,
    gpsHeightD1: 'GpsHt_ED_D1',
    gpsHeightJumpM: 2,
    downCrystals: [8],
    sampMs: [400, 600],
    speed: { min: null, max: 77 },
  },
  {
    family: 'heli',
    label: 'Helicopter (one magnetometer)',
    scripts: 'Heli_Scripts_V1.00',
    x: 'East_ED',
    y: 'North_ED',
    radalt: 'Radalt_ED',
    mag: 'MagTF1U_ED',
    magD4: 'MagTF1U_ED_D4',
    noiseD4: ['MagTF1U_ED_D4'],
    mags: ['MagTF1U_ED'],
    sats: 'Sats',
    eastD1: 'East_ED_D1',
    northD1: 'North_ED_D1',
    gpsJumpM: 3,
    gpsHeightD1: 'GpsHt_ED_D1',
    gpsHeightJumpM: 2,
    downCrystals: [4, 8],
    sampMs: [400, 1100],
    speed: { min: null, max: HELI_SPEED_MAX_MS },
  },
];

/**
 * Persistence of the daily mag noise checks (MB, 16 Oct 2026: more than 2 s, 40 fids at 20 Hz). Decision 179: only
 * those checks use it.
 */
export const QC_PERSISTENCE_S = 2;
/** Fewest GPS satellites a fiducial may use before it is flagged (decision 180), unless the project sets its own. */
export const DEFAULT_MIN_SATELLITES = 6;
/** The test keys whose fail Line review accepts only with a typed reason (decision 181). */
export const QC_REASON_TO_ACCEPT: readonly string[] = ['hard_deck'];
/** DGPS status when no corrections are received (MB, 16 Oct 2026: "stuck at 99"). */
export const DGPS_NO_CORRECTIONS = 99;
/** MagRatio pass band (magnetometer TF ÷ fluxgate TF, MB 16 Oct 2026). */
export const MAG_RATIO_BAND: readonly [number, number] = [0.95, 1.05];
/** The fluxgate's total field channel MagTF1U_ED is compared with (decision 504). */
export const VECTOR_TF_CHANNEL = 'VecTF';
/**
 * Decision 504: MagTF1U_ED against VecTF "close to identical (warn if not)". Close is MagRatio's pass band (MB, 16
 * Oct 2026: 0.95 to 1.05), so more than 5 % apart is a check.
 */
export const MAG_VECTOR_PERCENT = 5;
/** Decision 504: the magnetometers against each other "within 10 %"; more apart is a check. */
export const MAG_SENSORS_PERCENT = 10;

/** Decision 504's statistics-only channels, as MB named them, with their units (shown, never judged). */
export const QC_STATISTICS: readonly { readonly channel: string; readonly unit: string }[] = [
  { channel: 'Radalt', unit: 'm' },
  { channel: 'DTM', unit: 'm' },
  { channel: 'GPSHt_ED', unit: 'm' },
  { channel: 'BaroHPa', unit: 'hPa' },
  { channel: 'Temp', unit: '°C' },
  { channel: 'Humid', unit: '%' },
];

export function qcFamilyProfile(family: QcFamily): QcFamilyProfile {
  const p = QC_FAMILIES.find((f) => f.family === family);
  if (!p) throw new Error(`unknown QC family ${family}`);
  return p;
}

/** The family of an aircraft type ("Cessna Grand Caravan" → c208); null when it cannot be told. */
export function qcFamilyForAircraft(typeName: string | null | undefined, jobType?: string): QcFamily | null {
  const t = typeName ?? '';
  if (isCessna208(t)) return 'c208';
  if (/\b((c\s?|cessna\s+)210[a-z]?|centurion)\b/i.test(t)) return 'c210';
  if (jobType === 'helicopter' || /\b(heli|helicopter|as350|squirrel|jet ?ranger|bell|r44|r66)\b/i.test(t))
    return 'heli';
  return null;
}

/** The project's QC limits: its own (from the contract), else the tenant defaults for its aircraft type. */
export function projectQcRules(project: Pick<Project, 'qc_rules'>, aircraftType = ''): QcRule[] {
  const own = project.qc_rules;
  if (Array.isArray(own) && own.length > 0) return own.map((r) => ({ ...r }));
  return qcDefaultsForAircraftType(BENZ_F26048_QC_RULES, aircraftType);
}

/** Where the QC limits of a run came from (shown on the QC screen and kept with the run). */
export function qcRulesSource(project: Pick<Project, 'qc_rules'>): 'project' | 'tenant_defaults' {
  return Array.isArray(project.qc_rules) && project.qc_rules.length > 0 ? 'project' : 'tenant_defaults';
}

// ---------------------------------------------------------------------------------------- the run spec (Rust)

export type QcRuleKind =
  | { readonly kind: 'flagged' }
  | { readonly kind: 'abs_above'; readonly limit: number }
  | { readonly kind: 'above'; readonly limit: number }
  | { readonly kind: 'below'; readonly limit: number }
  | { readonly kind: 'outside'; readonly min: number; readonly max: number }
  | { readonly kind: 'equals'; readonly value: number }
  | { readonly kind: 'not_in'; readonly values: readonly number[] }
  /** The channel more than `percent` % from `other` (decision 504's comparisons); the event's value is in %. */
  | { readonly kind: 'differs_from'; readonly other: string; readonly percent: number }
  /** Statistics only (decision 504): the line's minimum, maximum, mean and range; never flagged. */
  | { readonly kind: 'stats' };

export type QcSeverity = 'fail' | 'warn';

export interface QcTestSpec {
  readonly key: string;
  readonly label: string;
  readonly channel: string;
  readonly rule: QcRuleKind;
  readonly min_length_m: number | null;
  readonly min_duration_s: number | null;
  readonly severity: QcSeverity;
  readonly measure: { readonly channel: string; readonly offset: number; readonly unit: string } | null;
}

export type GeosoftCheck =
  | {
      readonly gx: 'noise';
      readonly channel: string;
      readonly tolerance: number;
      readonly normalised: boolean;
    }
  | { readonly gx: 'density'; readonly separation_m: number }
  | {
      readonly gx: 'path';
      readonly lines_gdb: string;
      readonly alldev_m: number;
      readonly dist_m: number;
      /** N14: the flight's lines of one block, checked against its own `Lines_<block>.gdb`; absent: every line. */
      readonly lines?: readonly string[];
      readonly block?: string;
    }
  | {
      readonly gx: 'height';
      readonly channel: string;
      readonly nominal_m: number;
      readonly alldev_m: number;
      readonly dist_m: number;
      readonly groups: readonly { readonly nominal_m: number; readonly lines: readonly string[] }[];
    };

/** `QcRunSpec` of `crates/teslyn-geosoft/src/qc.rs`. */
export interface QcRunSpec {
  readonly label: string;
  readonly gdb: string;
  readonly fid_channel: string;
  readonly fid_seconds: number;
  readonly x_channel: string;
  readonly y_channel: string;
  readonly geosoft: readonly GeosoftCheck[];
  readonly tests: readonly QcTestSpec[];
  readonly timeout_s: number | null;
  /** The block's coordinate system from its lines file (EPSG), checked against the database (R26); null: not. */
  readonly expected_epsg: number | null;
  /** Decisions 868 and 879: the flight's databases in its other blocks' folders, checked in the same run. */
  readonly more_gdbs?: readonly string[];
}

export interface QcSpecInput {
  readonly label: string;
  /** The flight database (`<block>\Geosoft\Database\Flt09.gdb`). */
  readonly gdb: string;
  /** The planned-lines database for the flight path check (`Lines.gdb`); null skips the check. */
  readonly linesGdb: string | null;
  /** Each block's own planned-lines database, `Lines_<block key>.gdb` (N14, the H26036 pattern); none: `linesGdb`. */
  readonly blockLinesGdbs?: readonly BlockLinesGdb[] | null;
  /** Decisions 868 and 879: the flight's databases in its other blocks' folders (`geosoft_qc_sources`). */
  readonly moreGdbs?: readonly string[] | null;
  readonly family: QcFamily;
  readonly rules: readonly QcRule[];
  /** Blocks of the project with their planned terrain clearance (null = not set) and lines file zone (`epsg`). */
  readonly blocks: readonly (Pick<Block, 'digit' | 'name' | 'terrain_clearance_m' | 'kind'> &
    Partial<Pick<Block, 'epsg' | 'key'>>)[];
  /** Line names in the flight database (read natively before the run), for per-block clearances. */
  readonly lineNames: readonly string[];
  readonly hardDeckM: number | null;
  readonly lowerWorkingM: number | null;
  /** Fewest satellites before a fiducial is flagged (`project.qc_min_satellites`); default 6 (decision 180). */
  readonly minSatellites?: number | null;
  /** Daily mag noise channels (`project.qc_noise_channels`); default the family's `noiseD4` (decision 179). */
  readonly noiseChannels?: readonly string[] | null;
}

/** What could not be included, in plain words (shown before the run, kept with it). */
export interface QcSpecResult {
  readonly spec: QcRunSpec;
  readonly skipped: readonly string[];
}

const blockDigitOf = (lineName: string): string => lineName.replace(/^[A-Z]+/, '').charAt(0);

/**
 * The run spec of one flight: the contract GXs with the project's limits, the tests on their flags, and the
 * family's checks.
 */
export function geosoftQcSpec(input: QcSpecInput): QcSpecResult {
  const p = qcFamilyProfile(input.family);
  const skipped: string[] = [];
  const geosoft: GeosoftCheck[] = [];
  const tests: QcTestSpec[] = [];
  const on = (key: Parameters<typeof qcRule>[1]): QcRule | null => {
    const r = qcRule(input.rules, key);
    return r && r.enabled ? r : null;
  };
  const contract = (
    key: string,
    label: string,
    channel: string,
    overM: number | null,
    measure: QcTestSpec['measure'],
  ): QcTestSpec => ({
    key,
    label,
    channel,
    rule: { kind: 'flagged' },
    min_length_m: overM,
    min_duration_s: null,
    severity: 'fail',
    measure,
  });

  const path = on('flight_path');
  if (path) {
    // N14: each block's own Lines_<block>.gdb where it has one (qc-daily.ts), else the project's Lines.gdb
    const paths = qcFlightPathChecks({
      linesGdb: input.linesGdb,
      blockLinesGdbs: input.blockLinesGdbs ?? null,
      blocks: input.blocks.map((b) => ({ ...b, key: b.key ?? b.name })),
      lineNames: input.lineNames,
      alldevM: path.limit,
      distM: path.over_m ?? 0,
    });
    geosoft.push(...paths.checks);
    skipped.push(...paths.skipped);
    if (paths.checks.length > 0)
      tests.push(contract('flight_path', 'Flight path', 'Flag_Path', path.over_m, null));
  }

  const tc = on('terrain_clearance');
  const survey = input.blocks.filter((b) => b.kind === 'survey');
  const flown = new Set(input.lineNames.map(blockDigitOf));
  const flownBlocks = survey.filter((b) => flown.has(b.digit));
  const unset = flownBlocks.filter((b) => !(b.terrain_clearance_m && b.terrain_clearance_m > 0));
  if (tc && flownBlocks.length > 0 && unset.length === 0) {
    const nominal = (b: (typeof flownBlocks)[number]): number => b.terrain_clearance_m ?? 0;
    const base = nominal(flownBlocks[0]!);
    const groups = flownBlocks
      .filter((b) => nominal(b) !== base)
      .map((b) => ({
        nominal_m: nominal(b),
        lines: input.lineNames.filter((n) => blockDigitOf(n) === b.digit),
      }));
    geosoft.push({
      gx: 'height',
      channel: p.radalt,
      nominal_m: base,
      alldev_m: tc.limit,
      dist_m: tc.over_m ?? 0,
      groups,
    });
    tests.push(
      contract('terrain_clearance', 'Terrain clearance', 'Flag_Height', tc.over_m, {
        channel: p.radalt,
        offset: base,
        unit: 'm',
      }),
    );
  } else if (tc && unset.length > 0) {
    skipped.push(
      `Terrain clearance: enter the planned terrain clearance of ${unset.map((b) => b.name).join(', ')}.`,
    );
  }

  const noise = on('mag_noise_4th_diff');
  if (noise) {
    geosoft.push({ gx: 'noise', channel: p.mag, tolerance: noise.limit, normalised: false });
    tests.push(
      contract('mag_noise', 'Mag noise', 'Flag_Noise', noise.over_m, {
        channel: p.magD4,
        offset: 0,
        unit: 'nT',
      }),
    );
  }

  const minSats =
    input.minSatellites != null && input.minSatellites > 0 ? input.minSatellites : DEFAULT_MIN_SATELLITES;

  const sep = on('mag_sample_interval');
  if (sep) {
    geosoft.push({ gx: 'density', separation_m: sep.limit });
    tests.push(contract('sample_interval', 'Sample interval', 'Flag_Density', sep.over_m, null));
  }

  const check = (
    key: string,
    label: string,
    channel: string,
    rule: QcRuleKind,
    persistent: boolean,
    unit = '',
    severity: QcSeverity = 'warn',
  ): QcTestSpec => ({
    key,
    label,
    channel,
    rule,
    min_length_m: null,
    min_duration_s: persistent ? QC_PERSISTENCE_S : null,
    severity,
    measure: unit ? { channel, offset: 0, unit } : null,
  });
  // decision 179: the field staff's daily noise check of each magnetometer, the only checks with the 2 s rule
  const named = (input.noiseChannels ?? []).map((c) => c.trim()).filter((c) => c !== '');
  const noiseD4 = named.length > 0 ? named : p.noiseD4;
  if (noise) {
    noiseD4.forEach((channel, i) =>
      tests.push(
        check(
          `daily_noise_${i + 1}`,
          `Mag noise ${channel}`,
          channel,
          { kind: 'abs_above', limit: noise.limit },
          true,
          'nT',
        ),
      ),
    );
  } else skipped.push('Daily mag noise: the contract has no magnetometer noise limit.');
  tests.push(
    check(
      'gps_jump_east',
      'GPS easting jump',
      p.eastD1,
      { kind: 'abs_above', limit: p.gpsJumpM },
      false,
      'm',
    ),
    check(
      'gps_jump_north',
      'GPS northing jump',
      p.northD1,
      { kind: 'abs_above', limit: p.gpsJumpM },
      false,
      'm',
    ),
    check(
      'gps_height_jump',
      'GPS height jump',
      p.gpsHeightD1,
      { kind: 'abs_above', limit: p.gpsHeightJumpM },
      false,
      'm',
    ),
    check('dgps', 'DGPS corrections lost', 'DGPS', { kind: 'equals', value: DGPS_NO_CORRECTIONS }, false),
    check('satellites', `Fewer than ${minSats} satellites`, p.sats, { kind: 'below', limit: minSats }, false),
    check(
      'mag_ratio',
      'Mag ratio',
      'MagRatio',
      { kind: 'outside', min: MAG_RATIO_BAND[0], max: MAG_RATIO_BAND[1] },
      false,
      '',
    ),
    check('crystals', 'Down crystals', 'Dn', { kind: 'not_in', values: p.downCrystals }, false),
    check(
      'spectrometer_timing',
      'Spectrometer sample time',
      'Samp',
      { kind: 'outside', min: p.sampMs[0], max: p.sampMs[1] },
      false,
      'ms',
    ),
  );
  // decision 504: the tail magnetometer against the fluxgate, and the magnetometers against each other (C208)
  tests.push(
    check(
      'mag_vs_vector',
      `${p.mags[0]} against ${VECTOR_TF_CHANNEL}`,
      p.mags[0]!,
      { kind: 'differs_from', other: VECTOR_TF_CHANNEL, percent: MAG_VECTOR_PERCENT },
      false,
    ),
  );
  p.mags.forEach((a, i) =>
    p.mags
      .slice(i + 1)
      .forEach((b, k) =>
        tests.push(
          check(
            `mag_compare_${i + 1}_${i + k + 2}`,
            `${a} against ${b}`,
            a,
            { kind: 'differs_from', other: b, percent: MAG_SENSORS_PERCENT },
            false,
          ),
        ),
      ),
  );
  if (p.speed) {
    const rule: QcRuleKind =
      p.speed.min === null
        ? { kind: 'above', limit: p.speed.max }
        : { kind: 'outside', min: p.speed.min, max: p.speed.max };
    tests.push(check('speed', 'Speed', 'Speed', rule, false, 'm/s'));
  }
  // decision 181: a hard-deck breach is a fail, accepted in Line review only with a typed reason
  if (input.hardDeckM && input.hardDeckM > 0)
    tests.push(
      check(
        'hard_deck',
        'Below hard deck',
        p.radalt,
        { kind: 'below', limit: input.hardDeckM },
        false,
        'm',
        'fail',
      ),
    );
  else skipped.push('Hard deck: not set for this project.');
  if (input.lowerWorkingM && input.lowerWorkingM > 0)
    tests.push(
      check(
        'lower_working_height',
        'Below lower working height',
        p.radalt,
        { kind: 'below', limit: input.lowerWorkingM },
        false,
        'm',
      ),
    );
  else skipped.push('Lower working height: not set for this project.');
  // decision 504: statistics only, like the minimum GPS check's readout; they never change a line's verdict
  for (const st of QC_STATISTICS)
    tests.push({
      key: `stats_${st.channel.toLowerCase()}`,
      label: st.channel,
      channel: st.channel,
      rule: { kind: 'stats' },
      min_length_m: null,
      min_duration_s: null,
      severity: 'warn',
      measure: null,
    });

  return {
    spec: {
      label: input.label,
      gdb: input.gdb,
      fid_channel: 'ZFid_ms',
      fid_seconds: 0.001,
      x_channel: p.x,
      y_channel: p.y,
      geosoft,
      tests,
      timeout_s: null,
      expected_epsg: expectedEpsg(
        input.blocks.map((b) => ({ digit: b.digit, kind: b.kind, epsg: b.epsg ?? null })),
        flown,
      ),
      // decisions 868 and 879: the flight's other blocks' databases, in the same run (absent on one database)
      ...(input.moreGdbs && input.moreGdbs.length > 0 ? { more_gdbs: [...input.moreGdbs] } : {}),
    },
    skipped,
  };
}

// ---------------------------------------------------------------------------------------- results

export type QcVerdict = 'pass' | 'warn' | 'fail' | 'not_run';

export interface QcEvent {
  readonly fid_start: number;
  readonly fid_end: number;
  readonly length_m: number;
  readonly duration_s: number;
  readonly max_value: number | null;
  readonly sign: number;
}

export interface QcLineTest {
  readonly key: string;
  readonly label: string;
  readonly status: QcVerdict;
  readonly events: readonly QcEvent[];
  readonly hits: number;
  readonly short_runs: number;
  readonly reason?: string;
  /** A statistics-only test's readout of the line (decision 504; runs from 2026.10.13). */
  readonly stats?: QcChannelStats;
}

/** A channel's values on one line, dummies left out (`ChannelStats` in qc.rs). */
export interface QcChannelStats {
  readonly count: number;
  readonly min: number;
  readonly max: number;
  readonly mean: number;
  readonly range: number;
}

/** Whether a line test is a statistics-only readout (decision 504), not a check. */
export const isQcStatistics = (t: Pick<QcLineTest, 'key'>): boolean => t.key.startsWith('stats_');

/**
 * The statistics readout of a line (decision 504), one entry per channel the run summarised, in MB's order:
 * `Radalt 31.97 to 67.36 m, mean 49.67, range 35.39`; a channel the database has not got, or with no value on the
 * line, says so.
 */
export function qcStatisticsText(line: Pick<QcLineResult, 'tests'>): string[] {
  const n = (v: number): string => v.toLocaleString('en-AU', { maximumFractionDigits: 2 });
  return line.tests.filter(isQcStatistics).map((t) => {
    const unit = QC_STATISTICS.find((s) => s.channel.toLowerCase() === t.label.toLowerCase())?.unit ?? '';
    const u = unit === '' ? '' : unit === '%' || unit === '°C' ? unit : ` ${unit}`;
    if (!t.stats) return `${t.label}: ${t.reason || 'not run'}`;
    const s = t.stats;
    return `${t.label} ${n(s.min)} to ${n(s.max)}${u}, mean ${n(s.mean)}, range ${n(s.range)}`;
  });
}

/** `qc_run` (crates/teslyn-db/src/migrations/0010_geosoft_qc.sql). */
export interface QcRun extends Common {
  readonly project_id: Uuid;
  readonly flight_id: Uuid | null;
  readonly kind: 'flight_qc';
  readonly family: QcFamily;
  readonly label: string;
  readonly source_path: string;
  readonly source_bytes: number | null;
  readonly source_modified_at: IsoDateTime | null;
  readonly geosoft_version: string | null;
  readonly driver_version: string;
  readonly spec: Record<string, unknown>;
  readonly status: 'ok' | 'failed';
  readonly error: string;
  readonly started_at: IsoDateTime;
  readonly finished_at: IsoDateTime;
  readonly seconds: number;
  readonly line_count: number;
  readonly fail_count: number;
  readonly warn_count: number;
  readonly pass_count: number;
  readonly notes: readonly string[];
}

/** `qc_line_result`. */
export interface QcLineResult extends Common {
  readonly run_id: Uuid;
  readonly project_id: Uuid;
  readonly line_name: string;
  readonly line_number: number | null;
  readonly line_status_id: Uuid | null;
  readonly verdict: QcVerdict;
  readonly rows: number;
  readonly fid_start: number | null;
  readonly fid_end: number | null;
  readonly length_m: number;
  readonly tests: readonly QcLineTest[];
}

/** Zdas style of a Geosoft line name for the QC log: `L301560` → `L30156.0`, `T190220` → `T19022.0`. */
export function qcLogLine(lineName: string): string {
  const m = /^([A-Z])(\d+)$/.exec(lineName);
  if (!m || m[2]!.length < 2) return lineName;
  const d = m[2]!;
  return `${m[1]}${d.slice(0, -1)}.${d.slice(-1)}`;
}

const metres = (m: number): string => `${m.toFixed(1)}m`;
const seconds = (s: number): string => `${Number(s.toFixed(2))}s`;
const fid = (f: number): string => formatZfid(f);

/** The screen's name for this QC (MB's R91, 26 Oct 2026; the code keeps "Geosoft QC" in its names). */
export const FLIGHT_QC_NAME = 'Flight data QC';

/**
 * One exceedance as every list of them gives it (MB's R96): its zFid start and end, duration and distance, e.g.
 * `zFid 12135250 to 12152200, 16.95 s, 1,046.6 m` (zFids without separators, R129).
 */
export function qcEventText(e: Pick<QcEvent, 'fid_start' | 'fid_end' | 'duration_s' | 'length_m'>): string {
  const n = (v: number, d: number): string => v.toLocaleString('en-AU', { maximumFractionDigits: d });
  return `${zfidRangeText(e.fid_start, e.fid_end)}, ${n(e.duration_s, 2)} s, ${n(e.length_m, 1)} m`;
}

/**
 * The QC log sentences of the contract tests (PROJECT_FOLDER_STRUCTURE.md §9): one per test with events, events in
 * line order, `L30156.0 zFid 15933450-15960000 (1234.5m, 26.55s)` (the duration added by MB's R96), the line prefix
 * written once per line. The cause
 * ("due to terrain") is the operator's judgement and is left for them to add.
 */
export function qcLogSentences(lines: readonly QcLineResult[], severity: QcSeverity = 'fail'): string[] {
  const byTest = new Map<string, { label: string; parts: string[] }>();
  const sorted = [...lines].sort((a, b) => a.line_name.localeCompare(b.line_name));
  for (const l of sorted) {
    for (const t of l.tests) {
      if (t.events.length === 0) continue;
      if (severity === 'fail' ? t.status !== 'fail' : t.status !== 'warn') continue;
      const entry = byTest.get(t.key) ?? { label: t.label, parts: [] };
      t.events.forEach((e, i) => {
        const range = `${fid(e.fid_start)}-${fid(e.fid_end)}`;
        // R96: the distance and the duration of each exceedance
        const len = ` (${metres(e.length_m)}, ${seconds(e.duration_s)})`;
        entry.parts.push(i === 0 ? `${qcLogLine(l.line_name)} zFid ${range}${len}` : `${range}${len}`);
      });
      byTest.set(t.key, entry);
    }
  }
  return [...byTest.values()].map((e) => `${e.label} exceedances: ${e.parts.join(', ')}.`);
}

/** The reason Line review is given for a line the QC flagged (at most 100 characters, Line review's limit). */
export function qcReviewReason(line: QcLineResult): string {
  const failing = line.tests.filter((t) => t.status === 'fail' || t.status === 'warn');
  const parts = failing.map((t) => {
    const longest = Math.max(0, ...t.events.map((e) => e.length_m));
    const n = t.events.length;
    return longest > 0
      ? `${t.label.toLowerCase()} ${n} × (longest ${longest.toFixed(0)} m)`
      : `${t.label.toLowerCase()} ${n} ×`;
  });
  const text = `${FLIGHT_QC_NAME}: ${parts.join('; ')}`;
  return text.length <= 100 ? text : `${text.slice(0, 99)}…`;
}

/** Counts of a run's lines by verdict. */
export function qcVerdictCounts(lines: readonly Pick<QcLineResult, 'verdict'>[]): Record<QcVerdict, number> {
  const out: Record<QcVerdict, number> = { pass: 0, warn: 0, fail: 0, not_run: 0 };
  for (const l of lines) out[l.verdict] += 1;
  return out;
}

/**
 * The QC fails that stop a line being accepted without a typed reason (decision 181: a hard-deck breach), from the
 * newest QC result of the line's status. Each entry is a short description for the message, e.g. `below hard deck
 * 2 × (longest 350 m)`. An empty list means the line can be accepted as usual.
 */
export function qcAcceptBlockers(results: readonly QcLineResult[], lineStatusId: Uuid): string[] {
  const own = results.filter((r) => r.line_status_id === lineStatusId && r.deleted_at === null);
  if (own.length === 0) return [];
  const newest = own.reduce((a, b) => (b.created_at > a.created_at ? b : a));
  return newest.tests
    .filter((t) => QC_REASON_TO_ACCEPT.includes(t.key) && t.status === 'fail')
    .map((t) => {
      const longest = Math.max(0, ...t.events.map((e) => e.length_m));
      const n = t.events.length;
      const what = t.label.toLowerCase();
      return longest > 0 ? `${what} ${n} × (longest ${longest.toFixed(0)} m)` : what;
    });
}

/** The message shown when a line with `qcAcceptBlockers` is accepted without a reason (decision 181). */
export function qcAcceptReasonMessage(lineNumber: number, blockers: readonly string[]): string {
  return `A reason is required to accept line ${lineNumber}: ${FLIGHT_QC_NAME} found it ${blockers.join('; ')}.`;
}
